import type { MetadataRoute } from "next";
import urlJoin from "url-join";

import { env } from "@/env";

import { rewriteUrls } from "../../rewriteUrls/rewriteUrls";
import { SHOP_URL } from "../consts";

import type { NoIndexPaths } from "./getBuilderNoIndexPaths";

export type ProductSkuMapEntry = {
  locale: string;
  categoryUrlPath: string;
  productUrlKey: string;
};

export type ProductSkuMap = Map<string, ProductSkuMapEntry[]>;

export type CategoryMapEntry = {
  locale: string;
  categoryUrlPath: string;
};

export type CategoryMap = Map<string, CategoryMapEntry[]>;

export const getProductAlternates = (
  sku: string,
  currentLocale: string,
  skuMap: ProductSkuMap,
  noIndexPaths?: NoIndexPaths,
  parentSku?: string
): NonNullable<MetadataRoute.Sitemap[number]["alternates"]> | undefined => {
  const productsWithSku = skuMap.get(sku);

  if (!productsWithSku || productsWithSku.length === 0) {
    return undefined;
  }

  const languages: Record<string, string> = {};

  for (const product of productsWithSku) {
    // Skip the current locale
    if (product.locale === currentLocale) {
      continue;
    }

    const productSlug = urlJoin(SHOP_URL, product.categoryUrlPath, product.productUrlKey);

    // Skip alternates that are noIndex in their locale
    if (noIndexPaths?.has(product.locale, productSlug)) {
      continue;
    }

    // Skip variant alternates whose parent configurable product is noIndex
    if (noIndexPaths && parentSku) {
      const parentEntries = skuMap.get(parentSku);
      const parentEntry = parentEntries?.find((e) => e.locale === product.locale);
      if (parentEntry) {
        const parentSlug = urlJoin(SHOP_URL, parentEntry.categoryUrlPath, parentEntry.productUrlKey);
        if (noIndexPaths.has(product.locale, parentSlug)) {
          continue;
        }
      }
    }

    const productPath = urlJoin("/", productSlug);

    const rewrittenPath = rewriteUrls(productPath, product.locale);
    const alternateUrl = urlJoin(env.siteUrl, product.locale, rewrittenPath);

    languages[product.locale] = alternateUrl;
  }

  return Object.keys(languages).length > 0 ? { languages } : undefined;
};

export const getCategoryAlternates = (
  uid: string,
  currentLocale: string,
  categoryMap: CategoryMap,
  noIndexPaths?: NoIndexPaths
): NonNullable<MetadataRoute.Sitemap[number]["alternates"]> | undefined => {
  const categoriesWithUid = categoryMap.get(uid);

  if (!categoriesWithUid || categoriesWithUid.length === 0) {
    return undefined;
  }

  const languages: Record<string, string> = {};

  for (const category of categoriesWithUid) {
    // Skip the current locale
    if (category.locale === currentLocale) {
      continue;
    }

    const categorySlug = urlJoin(SHOP_URL, category.categoryUrlPath);

    // Skip alternates that are noIndex in their locale
    if (noIndexPaths?.has(category.locale, categorySlug)) {
      continue;
    }

    const categoryPath = urlJoin("/", categorySlug);

    const rewrittenPath = rewriteUrls(categoryPath, category.locale);
    const alternateUrl = urlJoin(env.siteUrl, category.locale, rewrittenPath);

    languages[category.locale] = alternateUrl;
  }

  return Object.keys(languages).length > 0 ? { languages } : undefined;
};
